import { useState } from 'react';
import {
  useIntegrations,
  useTasksFlat,
  useCompleteTask,
} from '../o365/hooks';
import type { M365TaskFlat } from '../o365/api';
import styles from './DashboardTasksTab.module.css';

// ── Datum formatieren ────────────────────────────────────────────────────────

function formatDue(isoOrDateTime: string | null): string | null {
  if (!isoOrDateTime) return null;
  try {
    const d = new Date(isoOrDateTime);
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const dStr = d.toISOString().slice(0, 10);
    const tomorrow = new Date(today.getTime() + 86_400_000).toISOString().slice(0, 10);

    if (dStr === todayStr) return 'Heute';
    if (dStr === tomorrow) return 'Morgen';
    if (dStr < todayStr) {
      const days = Math.round((today.getTime() - d.getTime()) / 86_400_000);
      return `Überfällig (${days}d)`;
    }
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return null;
  }
}

function isDue(isoOrDateTime: string | null): boolean {
  if (!isoOrDateTime) return false;
  try {
    return new Date(isoOrDateTime) < new Date();
  } catch {
    return false;
  }
}

// ── Einzelne Aufgaben-Zeile ───────────────────────────────────────────────────

function TaskRow({
  task,
  onComplete,
  isCompletePending,
}: {
  task: M365TaskFlat;
  onComplete: (t: M365TaskFlat) => void;
  isCompletePending: boolean;
}) {
  const dueDate = task.dueDateTime?.dateTime ?? null;
  const overdue = isDue(dueDate);
  const dueFmt = formatDue(dueDate);

  return (
    <div className={`${styles.taskRow} ${styles.taskRow_o365}`}>
      <span className={`${styles.badge} ${styles.badgeO365}`}>O365</span>

      <div className={styles.taskMain}>
        <span className={styles.taskTitle}>{task.title}</span>
        <div className={styles.taskMeta}>
          {task.listName && (
            <span className={styles.taskPlanLabel}>{task.listName}</span>
          )}
          {dueFmt && (
            <span className={`${styles.taskDue} ${overdue ? styles.taskDueOverdue : ''}`}>
              {dueFmt}
            </span>
          )}
          {task.importance === 'high' && (
            <span className={styles.taskImportance}>!</span>
          )}
        </div>
      </div>

      <div className={styles.taskActions}>
        <button
          type="button"
          className={styles.actionComplete}
          onClick={() => onComplete(task)}
          disabled={isCompletePending}
          title="Als erledigt markieren"
        >
          {isCompletePending ? '…' : '✓'}
        </button>
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────

export function DashboardTasksTab() {
  const { data: integrationsData } = useIntegrations();
  const isO365Connected =
    integrationsData?.data?.some(
      (i) => i.provider === 'MICROSOFT_365' && i.connected,
    ) ?? false;

  const { data: o365Data, isLoading } = useTasksFlat();
  const completeMutation = useCompleteTask();

  const [pendingComplete, setPendingComplete] = useState<string | null>(null);

  const tasks: M365TaskFlat[] = o365Data?.data ?? [];

  // Sortierung: Überfällige zuerst, dann nach Datum
  const sorted = [...tasks].sort((a, b) => {
    const aDate = a.dueDateTime?.dateTime ?? null;
    const bDate = b.dueDateTime?.dateTime ?? null;
    const aOver = isDue(aDate);
    const bOver = isDue(bDate);
    if (aOver && !bOver) return -1;
    if (!aOver && bOver) return 1;
    if (aDate && bDate) return aDate.localeCompare(bDate);
    if (aDate) return -1;
    if (bDate) return 1;
    return 0;
  });

  function handleComplete(task: M365TaskFlat) {
    setPendingComplete(task.id);
    completeMutation
      .mutateAsync({ listId: task.listId, taskId: task.id })
      .finally(() => setPendingComplete(null));
  }

  return (
    <div className={styles.root}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageHeaderTitle}>Aufgaben</h1>
          {sorted.length > 0 && (
            <span className={styles.pageHeaderCount}>{sorted.length} offen</span>
          )}
        </div>
      </div>

      {!isO365Connected && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>📋</span>
          <p className={styles.emptyTitle}>Microsoft 365 nicht verbunden</p>
          <p className={styles.emptySub}>
            Verbinden Sie Ihr Konto in Ihrem Profil, um Aufgaben anzuzeigen.
          </p>
        </div>
      )}

      {isO365Connected && isLoading && (
        <p className={styles.status}>Aufgaben werden geladen…</p>
      )}

      {isO365Connected && !isLoading && sorted.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>✅</span>
          <p className={styles.emptyTitle}>Keine offenen Aufgaben</p>
          <p className={styles.emptySub}>
            Alle Aufgaben aus Microsoft To Do sind erledigt.
          </p>
        </div>
      )}

      {isO365Connected && !isLoading && sorted.length > 0 && (
        <div className={styles.list}>
          {sorted.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onComplete={handleComplete}
              isCompletePending={pendingComplete === task.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
